import * as React from 'react';
import { Employee } from '../../../hooks/useEmployeeData';
import { downloadBlob } from '../export/ttExport';
import {
  WorkArea,
  ensureExcelJS,
  parseCSVText,
  parseExcelFile,
  buildMapping,
  isSampleRow,
  buildHeader,
  buildSamples,
  buildBlankRowFor,
  PreviewSummary,
} from './ttImportUtils';
import css from './TTImportWizard.module.css';

interface TTImportUploadStepProps {
  employees: Employee[];
  workAreas: WorkArea[];
  activeEmployeeId?: number | null;
  activeEmployeeName?: string | null;
  importFile: File | null;
  importParsing: boolean;
  ttFormBusy: boolean;
  localFileInputKey: number;
  importInputRef: React.RefObject<HTMLInputElement | null>;
  setImportFile: (f: File | null) => void;
  setImportParsing: (v: boolean) => void;
  setImportError: (v: string) => void;
  setImportHeader: (v: string[]) => void;
  setImportRows: (v: any[]) => void;
  setImportMapping: (v: Record<string, string>) => void;
  setImportSamples: (v: number) => void;
  setImportStep: (v: number) => void;
  setPreviewLoading: (v: boolean) => void;
  setPreviewError: (v: string) => void;
  setPreviewRows: (v: any[]) => void;
  setPreviewSummary: (v: PreviewSummary) => void;
  setTtFormBusy: (v: boolean) => void;
  children?: React.ReactNode;
}

export function TTImportUploadStep({
  employees,
  workAreas,
  activeEmployeeId,
  activeEmployeeName,
  importFile,
  importParsing,
  ttFormBusy,
  localFileInputKey,
  importInputRef,
  setImportFile,
  setImportParsing,
  setImportError,
  setImportHeader,
  setImportRows,
  setImportMapping,
  setImportSamples,
  setImportStep,
  setPreviewLoading,
  setPreviewError,
  setPreviewRows,
  setPreviewSummary,
  setTtFormBusy,
  children,
}: TTImportUploadStepProps): React.JSX.Element {
  const parseImportFile = async (file: File) => {
    setImportParsing(true);
    setImportError('');
    try {
      const name = file.name.toLowerCase();
      let aoa: any[][] = [];
      if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        aoa = await parseExcelFile(file);
      } else {
        const text = await file.text();
        aoa = parseCSVText(text);
      }
      if (!aoa || aoa.length === 0) {
        setImportError('Empty file or unreadable content');
        return;
      }
      const header = (aoa[0] || []).map((c: any) => String(c || ''));
      const mapping = buildMapping(header);
      const required = ['employee_id', 'work_area', 'day', 'start', 'end'];
      const missing = required.filter((k) => mapping[k] === undefined);
      if (missing.length > 0) {
        setImportError('Missing columns: ' + missing.join(', '));
        return;
      }
      const data = aoa.slice(1);
      let sampleCount = 0;
      const cleaned = data.filter((row) => {
        if (isSampleRow(row, mapping)) {
          sampleCount++;
          return false;
        }
        return row && row.some((v: any) => String(v).trim() !== '');
      });
      setImportHeader(header);
      setImportMapping(mapping);
      setImportRows(cleaned);
      setImportSamples(sampleCount);
      if (missing.length === 0) {
        setImportStep(2);
      }
    } catch (e: any) {
      setImportError(e?.message || 'Parse failed');
    } finally {
      setImportParsing(false);
    }
  };

  const onImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setImportFile(f);
    if (f) {
      setImportStep(1);
      setImportError('');
      setImportHeader([]);
      setImportRows([]);
      setImportMapping({});
      setImportSamples(0);
      setPreviewLoading(false);
      setPreviewError('');
      setPreviewRows([]);
      setPreviewSummary({ okNew: 0, okCr: 0, warn: 0, error: 0, skipped: 0 });
      parseImportFile(f);
    }
  };

  const handleImportClick = () => {
    setImportFile(null);
    if (importInputRef.current) {
      importInputRef.current.value = '';
      importInputRef.current.click();
    }
  };

  const handleDownloadTTForm = async () => {
    try {
      setTtFormBusy(true);
      const isSingle = !!activeEmployeeId;
      const rows: any[] = [];
      rows.push(buildHeader());
      const waList = (workAreas || []).map((wa) => wa.name).join('; ');
      buildSamples(waList).forEach((r) => rows.push(r));
      if (isSingle) {
        const empName = activeEmployeeName || `#${activeEmployeeId}`;
        for (let i = 0; i < 50; i++) rows.push(buildBlankRowFor(Number(activeEmployeeId), empName));
      } else {
        const sorted = [...employees].sort((a, b) => {
          const an = `${a.first_name} ${a.last_name}`.trim();
          const bn = `${b.first_name} ${b.last_name}`.trim();
          return an.localeCompare(bn);
        });
        sorted.forEach((emp) =>
          rows.push(buildBlankRowFor(emp.id, `${emp.first_name} ${emp.last_name}`))
        );
      }
      try {
        const ExcelJS = await ensureExcelJS();
        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('TimeTrackings');
        ws.addRows(rows);
        ws.views = [{ state: 'frozen', ySplit: 1, xSplit: 0 }];
        ws.columns = [
          { width: 12 },
          { width: 24 },
          { width: 16 },
          { width: 12 },
          { width: 8 },
          { width: 8 },
          { width: 12 },
          { width: 10 },
          { width: 10 },
          { width: 10 },
        ];
        try {
          if (workAreas && workAreas.length > 0) {
            const wsWA = wb.addWorksheet('WorkAreas');
            const waAoa = [
              ['WorkArea Name', 'WorkArea ID'],
              ...workAreas.map((wa) => [wa.name, wa.id]),
            ];
            wsWA.addRows(waAoa);
            const dvFormula = `WorkAreas!$A$2:$A$${workAreas.length + 1}`;
            for (let r = 4; r <= 1003; r++) {
              ws.getCell(`C${r}`).dataValidation = { type: 'list', formulae: [dvFormula] };
            }
          }
        } catch {}
        const buf = await wb.xlsx.writeBuffer();
        const base = isSingle
          ? `tt-form_${activeEmployeeName || `employee_${activeEmployeeId}`}`
          : 'tt-form_all-employees';
        downloadBlob(
          new Blob([buf], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          }),
          `${base}.xlsx`
        );
      } catch (e) {
        const sep = ';';
        const content =
          '\ufeff' +
          rows
            .map((r) =>
              r
                .map((c: any) => {
                  const s = String(c ?? '');
                  return s.includes(';') ||
                    s.includes(',') ||
                    s.includes('"') ||
                    s.includes('\n')
                    ? `"${s.replace(/"/g, '""')}"`
                    : s;
                })
                .join(sep)
            )
            .join('\r\n');
        const base = isSingle
          ? `tt-form_${activeEmployeeName || `employee_${activeEmployeeId}`}`
          : 'tt-form_all-employees';
        downloadBlob(
          new Blob([content], { type: 'text/csv;charset=utf-8' }),
          `${base}.csv`
        );
      }
    } finally {
      setTtFormBusy(false);
    }
  };

  return (
    <>
      {/* Download Form Section */}
      <div className={css.sectionCard}>
        <div className={css.sectionLabel}>Download form</div>
        <div className={css.sectionHint}>
          {activeEmployeeId
            ? `for employee ${activeEmployeeName || `#${activeEmployeeId}`}`
            : 'for all employees (alphabetical)'}
        </div>
        <button
          className={css.btnSecondary}
          onClick={handleDownloadTTForm}
          disabled={ttFormBusy || workAreas.length === 0 || employees.length === 0}
        >
          Download form
        </button>
      </div>

      {/* Import TT Data Section */}
      <div className={css.sectionCard}>
        <div className={css.sectionLabel}>Import TT data</div>
        <div className={css.sectionHint}>Please choose a file (.xlsx or .csv)</div>
        <input
          key={localFileInputKey}
          ref={importInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={onImportFileChange}
          className={css.hiddenInput}
          data-testid="tt-import-file-input"
        />
        <div className={css.fileRow}>
          <button
            className={css.btnSecondary}
            onClick={handleImportClick}
            data-testid="tt-import-choose-file-button"
          >
            Choose file
          </button>
          <span className={css.fileName}>
            {importFile ? importFile.name : 'No file chosen'}
          </span>
          <button
            className={css.btnPrimary}
            disabled={!importFile || importParsing}
            onClick={() => importFile && parseImportFile(importFile)}
            data-testid="tt-import-continue-button"
          >
            {importParsing ? 'Parsing\u2026' : 'Continue'}
          </button>
        </div>
        {children}
      </div>
    </>
  );
}
